import {
    AQI_CATEGORY_UPPER_BOUNDS,
    AFTERNOON_END_HOUR,
    DEFAULT_OUTDOOR_SOURCES,
    MORNING_END_HOUR,
    WEATHER_ICON_CLASS,
} from './constants';
import {
    AqiDisplayCategory,
    GreetingKey,
    OutdoorCityCondition,
    OutdoorCityTab,
    OutdoorConditionsPayload,
    OutdoorSummary,
} from './types';

export const getAqiCategory = (aqi: number | null | undefined): AqiDisplayCategory => {
    if (aqi === null || aqi === undefined) {
        return 'empty';
    }
    const match = AQI_CATEGORY_UPPER_BOUNDS.find((bound) => aqi <= bound.max);
    return match ? match.category : 'hazardous';
};


export const getGreetingKey = (hour: number): GreetingKey => {
    if (hour < MORNING_END_HOUR) {
        return 'good_morning';
    }
    if (hour < AFTERNOON_END_HOUR) {
        return 'good_afternoon';
    }
    return 'good_evening';
};

export const filterOutdoorCities = (
    cities: OutdoorCityCondition[],
    tab: OutdoorCityTab,
): OutdoorCityCondition[] => {
    void tab;
    return cities;
};

export const summarizeOutdoorCities = (cities: OutdoorCityCondition[]): OutdoorSummary => {
    if (cities.length === 0) {
        return { maxAqi: null, heavy: 0, medium: 0, light: 0, clear: 0, cityCount: 0 };
    }
    return cities.reduce<OutdoorSummary>(
        (summary, city) => ({
            maxAqi:
                city.aqi === null
                    ? summary.maxAqi
                    : summary.maxAqi === null
                    ? city.aqi
                    : Math.max(summary.maxAqi, city.aqi),
            heavy: summary.heavy + (city.smokeLevel === 'heavy' ? 1 : 0),
            medium: summary.medium + (city.smokeLevel === 'medium' ? 1 : 0),
            light: summary.light + (city.smokeLevel === 'light' ? 1 : 0),
            clear: summary.clear + (city.smokeLevel === 'clear' ? 1 : 0),
            cityCount: summary.cityCount + 1,
        }),
        { maxAqi: null, heavy: 0, medium: 0, light: 0, clear: 0, cityCount: 0 },
    );
};

const normalizeOutdoorCity = (raw: OutdoorCityCondition | Record<string, unknown>): OutdoorCityCondition => {
    const row = raw as OutdoorCityCondition;
    return {
        id: String(row.id || ''),
        city: String(row.city || ''),
        state: typeof row.state === 'string' ? row.state : '',
        region: row.region,
        siteCount: typeof row.siteCount === 'number' ? row.siteCount : 0,
        aqi: typeof row.aqi === 'number' ? row.aqi : null,
        temperatureC: typeof row.temperatureC === 'number' ? row.temperatureC : null,
        weather: row.weather ?? null,
        windDirection: typeof row.windDirection === 'string' ? row.windDirection : '',
        windSpeed: typeof row.windSpeed === 'number' ? row.windSpeed : null,
        smokeLevel: row.smokeLevel ?? null,
        forecast: Array.isArray(row.forecast) ? row.forecast : [],
        watchlisted: Boolean(row.watchlisted),
    };
};

export const normalizeOutdoorConditionsPayload = (raw: unknown): OutdoorConditionsPayload => {
    const body = (raw || {}) as Partial<OutdoorConditionsPayload>;
    return {
        updatedAt: typeof body.updatedAt === 'string' ? body.updatedAt : '',
        sources:
            Array.isArray(body.sources) && body.sources.length > 0 ? body.sources : [...DEFAULT_OUTDOOR_SOURCES],
        cities: Array.isArray(body.cities) ? body.cities.map(normalizeOutdoorCity) : [],
    };
};

export const weatherIconClass = (weather: OutdoorCityCondition['weather']): string => {
    if (!weather) {
        return '';
    }
    return WEATHER_ICON_CLASS[weather] ?? '';
};
